import { useEffect, useState } from 'react';

import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

/**
 * Firma pokazowa dla zespołów partnerów (migracja 883).
 * ====================================================
 *
 * Każdy aktywny członek zespołu aktywnego partnera ma członkostwo w jednej
 * firmie pokazowej — nadaje je i zdejmuje baza (`partner_demo_uzgodnij()`),
 * na podstawie kartoteki zespołu, bez zapraszania. Ten hook odpowiada tylko na
 * pytanie ekranu: „czy zalogowany ma do niej wejście i jak się nazywa”.
 *
 * Identyfikator czyta z kartoteki `partner_demo_organizacja` (odczyt dla
 * każdego zalogowanego), a wejście potwierdza WŁASNĄ listą organizacji
 * zalogowanego — bez członkostwa pozycja „Konto demo” się nie pokazuje, nawet
 * gdy kartoteka jest czytelna.
 */
export interface FirmaPokazowa {
  /** Organizacja firmy pokazowej, jeśli zalogowany jest jej aktywnym członkiem. */
  organizationId: string | null;
  nazwa: string | null;
}

export function useFirmaPokazowa(): FirmaPokazowa {
  const { user, organizations } = useAuth();
  const [kartoteka, setKartoteka] = useState<string | null>(null);

  useEffect(() => {
    let aktualne = true;
    if (!user) {
      setKartoteka(null);
      return;
    }
    supabase
      .from('partner_demo_organizacja')
      .select('organization_id')
      .maybeSingle()
      .then(({ data, error }) => {
        if (!aktualne) return;
        if (error) {
          // Brak kartoteki nie psuje panelu — pozycja po prostu się nie pokaże.
          console.warn('Firma pokazowa: odczyt kartoteki nie powiódł się:', error.message);
          setKartoteka(null);
          return;
        }
        setKartoteka((data as { organization_id: string } | null)?.organization_id ?? null);
      });
    return () => {
      aktualne = false;
    };
  }, [user]);

  const czlonkostwo = kartoteka
    ? organizations.find((o) => o.organization_id === kartoteka && o.status === 'active')
    : undefined;

  return {
    organizationId: czlonkostwo ? kartoteka : null,
    nazwa: czlonkostwo?.organization?.name ?? null,
  };
}

export default useFirmaPokazowa;
